import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trophy, XCircle, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DEV-019 · Opportunity Outcome section for the Operations Project
 * Detail. Presents the current outcome (Active / Won / Lost),
 * exposes Won / Lost toggles and — when Lost — surfaces the Lost
 * Reason picker with "Other → Detail required" client-side guard.
 *
 * Authorisation mirrors the backend PATCH /opportunity contract:
 * Operations (MANAGE_PROJECTS) and Sales (within visibility scope)
 * may transition. Customers / Installers never see this section.
 *
 * The section deliberately does NOT mutate project state, phases,
 * tasks or Sales attribution — outcome is a pure commercial marker
 * that lives alongside the delivery lifecycle.
 */
const REASON_LABEL_FALLBACK = {
    price: "Price",
    competitor: "Competitor",
    customer_deferred: "Customer Deferred",
    finance: "Finance",
    no_response: "No Response",
    property_technical: "Property / Technical Suitability",
    customer_changed_mind: "Customer Changed Mind",
    duplicate_invalid: "Duplicate / Invalid Lead",
    other: "Other",
};

function OutcomeBadge({ outcome }) {
    if (outcome === "won") return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]"
              data-testid="ops-outcome-badge-won">
            <Trophy size={11} /> Won
        </span>
    );
    if (outcome === "lost") return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]"
              data-testid="ops-outcome-badge-lost">
            <XCircle size={11} /> Lost
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBE1F7]"
              data-testid="ops-outcome-badge-active">
            Active
        </span>
    );
}

export default function OpportunityOutcomeSection({ project, onProjectUpdated }) {
    const { user } = useAuth();
    const canEdit = useMemo(() => {
        if (!user) return false;
        const role = (user.role || "").toLowerCase();
        return role === "operations" || role === "sales"
            || (Array.isArray(user.permissions) && user.permissions.includes("manage_projects"));
    }, [user]);

    const outcome = project?.opportunity_outcome || "active";
    const [reasons, setReasons] = useState([]);
    const [showLostPicker, setShowLostPicker] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({
        lost_reason: project?.lost_reason || "",
        lost_reason_detail: project?.lost_reason_detail || "",
    });

    useEffect(() => {
        setForm({
            lost_reason: project?.lost_reason || "",
            lost_reason_detail: project?.lost_reason_detail || "",
        });
        setShowLostPicker(false);
        setErr("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, outcome]);

    useEffect(() => {
        if (!showLostPicker) return;
        (async () => {
            try {
                const r = await api.listLostReasons().catch(() => ({ reasons: [] }));
                setReasons(r?.reasons || []);
            } catch { /* non-fatal */ }
        })();
    }, [showLostPicker]);

    // Guard AFTER hooks so rules-of-hooks stays satisfied when the
    // caller lacks permission.
    if (!canEdit) return null;

    const patch = async (payload) => {
        setSaving(true);
        setErr("");
        try {
            const updated = await api.updateOpportunityOutcome(project.id, payload);
            onProjectUpdated?.(updated);
            toast.success(`Opportunity marked ${payload.outcome}`);
            setShowLostPicker(false);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const markWon = () => patch({ outcome: "won" });
    const markLostSubmit = () => {
        if (!form.lost_reason) {
            setErr("Select a Lost Reason.");
            return;
        }
        if (form.lost_reason === "other" && !(form.lost_reason_detail || "").trim()) {
            setErr("Lost Reason Detail is required when Lost Reason = Other.");
            return;
        }
        patch({
            outcome: "lost",
            lost_reason: form.lost_reason,
            lost_reason_detail: (form.lost_reason_detail || "").trim() || null,
        });
    };
    const reopen = () => patch({ outcome: "active" });

    const reasonLabel = (key) => (reasons.find((r) => r.key === key) || {}).label || REASON_LABEL_FALLBACK[key] || key;

    return (
        <section
            className="rounded-xl border border-[#E5E7EB] bg-white p-5"
            data-testid="ops-opportunity-outcome"
            data-outcome={outcome}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <Trophy size={14} strokeWidth={1.75} />
                    </span>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Opportunity outcome
                    </div>
                </div>
                <OutcomeBadge outcome={outcome} />
            </div>

            {outcome === "active" && !showLostPicker && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <button
                        type="button"
                        onClick={markWon}
                        disabled={saving}
                        data-testid="ops-outcome-mark-won"
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#166534] text-white hover:bg-[#14532D] disabled:opacity-60"
                    >
                        <Trophy size={13} /> Mark Won
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowLostPicker(true)}
                        disabled={saving}
                        data-testid="ops-outcome-mark-lost"
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-60"
                    >
                        <XCircle size={13} /> Mark Lost
                    </button>
                </div>
            )}

            {outcome === "active" && showLostPicker && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                        <label className="block text-xs text-[#4B5565] mb-1">Lost reason</label>
                        <select
                            data-testid="ops-outcome-lost-reason-select"
                            value={form.lost_reason}
                            onChange={(e) => setForm({ ...form, lost_reason: e.target.value })}
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Select…</option>
                            {(reasons.length
                                ? reasons
                                : Object.entries(REASON_LABEL_FALLBACK).map(([key, label]) => ({ key, label }))
                            ).map((r) => (
                                <option key={r.key} value={r.key}
                                    data-testid={`ops-outcome-lost-reason-opt-${r.key}`}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {form.lost_reason && (
                        <div>
                            <label className="block text-xs text-[#4B5565] mb-1">
                                Detail{form.lost_reason === "other" ? " (required)" : " (optional)"}
                            </label>
                            <input
                                type="text"
                                data-testid="ops-outcome-lost-reason-detail"
                                value={form.lost_reason_detail}
                                onChange={(e) => setForm({ ...form, lost_reason_detail: e.target.value })}
                                maxLength={280}
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                    )}
                    {!!err && (
                        <div className="md:col-span-2 text-xs text-[#DA3535]" data-testid="ops-outcome-error">
                            {err}
                        </div>
                    )}
                    <div className="md:col-span-2 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => { setShowLostPicker(false); setErr(""); }}
                            disabled={saving}
                            data-testid="ops-outcome-lost-cancel"
                            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#131B2F]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={markLostSubmit}
                            disabled={saving}
                            data-testid="ops-outcome-lost-save"
                            className="text-xs px-2.5 py-1 rounded-md bg-[#B91C1C] text-white disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Confirm Lost"}
                        </button>
                    </div>
                </div>
            )}

            {(outcome === "won" || outcome === "lost") && (
                <div className="text-sm space-y-2">
                    {outcome === "lost" && project?.lost_reason && (
                        <div className="text-[#131B2F]" data-testid="ops-outcome-lost-summary">
                            <span className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Reason</span>
                            <div className="mt-0.5">
                                {reasonLabel(project.lost_reason)}
                                {project.lost_reason_detail && (
                                    <div className="text-xs text-[#4B5565]" data-testid="ops-outcome-lost-detail-value">
                                        {project.lost_reason_detail}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={reopen}
                        disabled={saving}
                        data-testid="ops-outcome-reopen"
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-60"
                    >
                        <RotateCcw size={13} /> Reopen (mark Active)
                    </button>
                    {!!err && (
                        <div className="text-xs text-[#DA3535]" data-testid="ops-outcome-error">{err}</div>
                    )}
                </div>
            )}
        </section>
    );
}
